// =============================================================================
//  useTriggerScan.ts - hook qui declenche un scan, hybride live/demo.
//
//  - En live  : POST /api/clients/{id}/scans + poll /api/scans/{scanId} jusqu a
//               status=done, puis invalide les queries TanStack pour rafraichir
//               le dashboard.
//  - En demo  : runMockScan + saveScan synchrone (existant).
// =============================================================================

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IS_LIVE_MODE } from '../api/liveMode';
import { nis365Api } from '../api/nis365Client';
import { runMockScan } from '../domain/scanEngine';
import { saveScan } from '../domain/scanStore';
import { Scan, Tenant } from '../domain/types';

interface TriggerScanArgs {
  tenant:   Tenant;
  scanType?: 'full' | 'partial';
}

/** Poll un scan API jusqu a status final (done | error) ou timeout. */
async function pollScan(scanId: string, maxWaitSec = 180): Promise<Scan> {
  const intervalMs = 4000;
  const startedAt = Date.now();
  while ((Date.now() - startedAt) / 1000 < maxWaitSec) {
    const s = await nis365Api.getScan(scanId);
    if (s) {
      // Heuristique : si on a des results et que les statuses ne sont pas tous
      // queued/running, c'est fini (le back update finishedAt mais notre Scan
      // mapper le set au startedAt si null, donc on ne peut pas comparer dates).
      // On s'appuie sur la presence de results > 0.
      if (s.results.length > 0 && s.results.some((r) => r.status !== 'manual')) {
        return s;
      }
    }
    await new Promise((res) => setTimeout(res, intervalMs));
  }
  throw new Error(`Scan ${scanId} timeout apres ${maxWaitSec}s`);
}

export function useTriggerScan() {
  const queryClient = useQueryClient();

  return useMutation<Scan, Error, TriggerScanArgs>({
    mutationFn: async ({ tenant, scanType = 'full' }) => {
      if (IS_LIVE_MODE) {
        const initial = await nis365Api.triggerScan(tenant.id, scanType);
        // Le scan est en status queued/running, on poll jusqu a la fin
        return pollScan(initial.id);
      } else {
        // Mode demo : synchrone
        const scan = runMockScan(tenant, { triggeredBy: 'ui' });
        saveScan(scan);
        return scan;
      }
    },
    onSuccess: (_scan, vars) => {
      // Invalide les queries dependantes pour refresh auto du dashboard / pages
      if (IS_LIVE_MODE) {
        queryClient.invalidateQueries({ queryKey: ['scans', vars.tenant.id] });
        queryClient.invalidateQueries({ queryKey: ['clients'] });
      }
    },
  });
}
